/**
 * Enhanced Statistics Dashboard Component
 * Displays comprehensive system statistics with growth metrics
 */

import React, { useState, useEffect } from 'react';
import { analyticsService, EnhancedSystemStatistics } from '../services/analytics.service';

interface EnhancedStatisticsDashboardProps {
  manufacturerId?: string; // If provided, show manufacturer-specific analytics
  filters?: {
    startDate?: string;
    endDate?: string;
  };
}

export const EnhancedStatisticsDashboard: React.FC<EnhancedStatisticsDashboardProps> = ({
  manufacturerId,
  filters
}) => {
  const [statistics, setStatistics] = useState<EnhancedSystemStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatistics();
  }, [manufacturerId, filters]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If manufacturerId is provided, get manufacturer-specific statistics
      // Otherwise, get system-wide statistics
      const data = await analyticsService.getSystemStatistics(manufacturerId, filters);
        
      setStatistics(data);
    } catch (err) {
      setError(manufacturerId ? 'Failed to fetch manufacturer statistics' : 'Failed to fetch system statistics');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatGrowth = (growth: number) => {
    const isPositive = growth >= 0;
    const color = isPositive ? 'text-green-600' : 'text-red-600';
    const icon = isPositive ? '↗' : '↘';
    return (
      <span className={`${color} text-sm font-medium`}>
        {icon} {Math.abs(growth).toFixed(1)}%
      </span>
    );
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    subtitle?: string;
    growth?: number;
    color: 'blue' | 'green' | 'purple' | 'yellow' | 'red' | 'indigo';
    format?: 'number' | 'percentage';
  }> = ({ title, value, subtitle, growth, color, format = 'number' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200'
    };

    const formatValue = (val: number) => {
      if (format === 'percentage') {
        return `${val.toFixed(1)}%`;
      }
      return val.toLocaleString();
    };

    return (
      <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">{title}</div>
          {growth !== undefined && formatGrowth(growth)}
        </div>
        <div className="text-2xl font-bold mt-1">
          {formatValue(value)}
        </div>
        {subtitle && (
          <div className="text-xs opacity-75 mt-1">{subtitle}</div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No statistics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Header */}
      {manufacturerId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-sm text-blue-800">
              Showing analytics for selected manufacturer
            </p>
          </div>
        </div>
      )}
      
      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Products"
          value={statistics.totalProducts}
          growth={statistics.periodComparison?.growthMetrics.productsGrowth}
          color="blue"
          subtitle="All products created"
        />
        <StatCard
          title="Activated Products"
          value={statistics.activatedProducts}
          growth={statistics.periodComparison?.growthMetrics.activationsGrowth}
          color="green"
          subtitle="Products ready for use"
        />
        <StatCard
          title="Consumed Products"
          value={statistics.consumedProducts}
          growth={statistics.periodComparison?.growthMetrics.consumptionsGrowth}
          color="purple"
          subtitle="Products consumed by users"
        />
        <StatCard
          title="Total Scans"
          value={statistics.totalScans}
          growth={statistics.periodComparison?.growthMetrics.scansGrowth}
          color="yellow"
          subtitle="QR code scans"
        />
      </div>

      {/* Secondary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Batches"
          value={statistics.totalBatches}
          color="indigo"
          subtitle="Product batches created"
        />
        <StatCard
          title="Total Dealers"
          value={statistics.totalDealers}
          color="red"
          subtitle="Registered dealers"
        />
        <StatCard
          title="Total Packages"
          value={statistics.totalPackages}
          color="blue"
          subtitle="Cases and pallets"
        />
      </div>

      {/* Rate Statistics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Rates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Activation Rate"
            value={statistics.activationRate}
            color="green"
            format="percentage"
            subtitle="Products activated vs created"
          />
          <StatCard
            title="Consumption Rate"
            value={statistics.consumptionRate}
            color="purple"
            format="percentage"
            subtitle="Products consumed vs created"
          />
          <StatCard
            title="Scan Engagement"
            value={statistics.scanEngagementRate}
            color="yellow"
            format="percentage"
            subtitle="Scans per product"
          />
          <StatCard
            title="Package Activation"
            value={statistics.packageActivationRate}
            color="blue"
            format="percentage"
            subtitle="Packages activated vs created"
          />
        </div>
      </div>

      {/* Period Comparison */}
      {statistics.periodComparison && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Period Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Period */}
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Current Period</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Products:</span>
                  <span className="font-medium">{statistics.totalProducts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Activations:</span>
                  <span className="font-medium">{statistics.activatedProducts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Consumptions:</span>
                  <span className="font-medium">{statistics.consumedProducts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Scans:</span>
                  <span className="font-medium">{statistics.totalScans.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Previous Period */}
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Previous Period</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Products:</span>
                  <span className="font-medium">
                    {statistics.periodComparison.previousPeriod.totalProducts.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Activations:</span>
                  <span className="font-medium">
                    {statistics.periodComparison.previousPeriod.activatedProducts.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Consumptions:</span>
                  <span className="font-medium">
                    {statistics.periodComparison.previousPeriod.consumedProducts.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Scans:</span>
                  <span className="font-medium">
                    {statistics.periodComparison.previousPeriod.totalScans.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Growth Summary */}
          <div className="mt-6 pt-4 border-t">
            <h4 className="text-md font-medium text-gray-700 mb-3">Growth Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600">Products</div>
                <div className="text-lg font-semibold">
                  {formatGrowth(statistics.periodComparison.growthMetrics.productsGrowth)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Activations</div>
                <div className="text-lg font-semibold">
                  {formatGrowth(statistics.periodComparison.growthMetrics.activationsGrowth)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Consumptions</div>
                <div className="text-lg font-semibold">
                  {formatGrowth(statistics.periodComparison.growthMetrics.consumptionsGrowth)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Scans</div>
                <div className="text-lg font-semibold">
                  {formatGrowth(statistics.periodComparison.growthMetrics.scansGrowth)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedStatisticsDashboard;